/**
 * The stocktake's own calls, in the shape of `src/api.ts`: bearer token on
 * every request, a typed error so a 403 on finalise reads as "your role does
 * not allow that" rather than "something went wrong".
 */

import type { CountHeader, CountLine, CountMethod, StockLevel } from "@baros/core";
import { request as shared } from "../api.js";

async function request<T>(
  token: string,
  method: "GET" | "PUT" | "POST",
  path: string,
  body?: unknown,
): Promise<T> {
  // The shared client: it refreshes an expired token once and ends
  // the session when it cannot (see `request` in src/api.ts).
  return shared<T>(token, method, path, body);
}

const v = (venueId: string) => encodeURIComponent(venueId);

/** What the tablet sends for one line. Millilitres are the server's business. */
export type LineReading =
  | { readonly method: "tenths"; readonly bottles: number; readonly tenths: number }
  | { readonly method: "weigh"; readonly bottles: number; readonly grams: number };

export const stockApi = {
  levels: (token: string, venueId: string) =>
    request<{ stock: StockLevel[] }>(token, "GET", `/v1/venues/${v(venueId)}/stock`),

  counts: (token: string, venueId: string) =>
    request<{ counts: CountHeader[] }>(token, "GET", `/v1/venues/${v(venueId)}/counts`),

  count: (token: string, venueId: string, countId: string) =>
    request<{ count: CountHeader; lines: CountLine[] }>(
      token,
      "GET",
      `/v1/venues/${v(venueId)}/counts/${encodeURIComponent(countId)}`,
    ),

  startCount: (token: string, venueId: string, method: CountMethod) =>
    request<{ count: CountHeader; stock: StockLevel[] }>(
      token,
      "POST",
      `/v1/venues/${v(venueId)}/counts`,
      { method },
    ),

  saveLine: (
    token: string,
    venueId: string,
    countId: string,
    ingredientId: string,
    reading: LineReading,
  ) =>
    request<{ line: CountLine; lineCount: number }>(
      token,
      "PUT",
      `/v1/venues/${v(venueId)}/counts/${encodeURIComponent(countId)}/lines/${encodeURIComponent(ingredientId)}`,
      reading,
    ),

  finalise: (token: string, venueId: string, countId: string) =>
    request<{ count: CountHeader; applied: number }>(
      token,
      "POST",
      `/v1/venues/${v(venueId)}/counts/${encodeURIComponent(countId)}/finalise`,
    ),

  adjust: (
    token: string,
    venueId: string,
    ingredientId: string,
    body: { heldMl: number; location?: string },
  ) =>
    request<{ level: StockLevel }>(
      token,
      "PUT",
      `/v1/venues/${v(venueId)}/stock/${encodeURIComponent(ingredientId)}`,
      body,
    ),
};
